/* @jsx h */
import { defineComponent, h, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import {
  requestCreateLoginToken,
  verifyLoginCode,
  fetchCoinOffer,
  claimCoins,
  type CoinOfferResponse,
} from "@app/server/client";
import { CoinOfferCard, CoinOfferSkeleton } from "../../components";
import { CustomerLanguageSelector } from "../../components/CustomerLanguageSelector";
import { useCustomer } from "../../providers/auth-context";

type Stage = "email" | "code" | "done";

/**
 * The claim funnel.
 *
 * Reached from a receipt's QR with `?payment=<guid>`. Coins are minted at claim,
 * so the coins on offer here do not exist anywhere until this form is completed —
 * which is exactly why the offer is stated before anything is asked for.
 *
 * The OTP itself is the existing `/email_tokens` flow; this page does not
 * introduce a second way to sign in.
 */
export const SignupPage = defineComponent({
  name: "SignupPage",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useLocalization();
    const auth = useCustomer();

    const paymentGuid = computed(() => (route.query.payment as string) || "");

    const stage = ref<Stage>("email");
    const email = ref("");
    const code = ref("");
    const termsAccepted = ref(false);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const offer = ref<CoinOfferResponse | null>(null);
    // Distinct from `offer === null`, which is also the resting state for a
    // signup reached without a payment guid.
    const offerLoading = ref(Boolean(route.query.payment));
    const claimedCount = ref(0);

    onMounted(async () => {
      if (!paymentGuid.value) {
        offerLoading.value = false;
        return;
      }
      try {
        offer.value = await fetchCoinOffer(paymentGuid.value);
      } catch (e) {
        console.warn("Failed to load coin offer:", e);
      } finally {
        offerLoading.value = false;
      }
    });

    const emailValid = computed(() => /^\S+@\S+\.\S+$/.test(email.value.trim()));

    const requestCode = async () => {
      if (!emailValid.value || !termsAccepted.value) return;
      loading.value = true;
      error.value = null;
      try {
        await requestCreateLoginToken(email.value.trim());
        stage.value = "code";
      } catch (err: any) {
        error.value =
          err?.message === "429"
            ? t("customer.coins.signup_rate_limited")
            : t("customer.coins.signup_send_failed");
      } finally {
        loading.value = false;
      }
    };

    const verifyAndClaim = async () => {
      if (code.value.trim().length < 4) return;
      loading.value = true;
      error.value = null;
      try {
        await verifyLoginCode(email.value.trim(), code.value.trim());
        await auth.refresh();
      } catch (err: any) {
        error.value = t("customer.coins.signup_invalid_code");
        loading.value = false;
        return;
      }

      // The account exists either way; a failed claim should not read as a
      // failed signup, so these are reported separately.
      try {
        if (paymentGuid.value) {
          const result = await claimCoins(paymentGuid.value);
          claimedCount.value = result.claimed;
          if (
            result.status !== "claimed" &&
            result.status !== "already_claimed"
          ) {
            error.value = t(`customer.coins.refused_${result.status}`);
          }
        }
        stage.value = "done";
      } catch (e) {
        error.value = t("customer.coins.claim_failed");
        stage.value = "done";
      } finally {
        loading.value = false;
      }
    };

    const renderOffer = () => {
      if (offerLoading.value) return <CoinOfferSkeleton />;

      return offer.value && offer.value.programs.length > 0 ? (
        <CoinOfferCard
          offers={offer.value.programs}
          // A receipt someone else already collected must not still read
          // "Collect 2" — the claim would only refuse on submit.
          collected={stage.value === "done" || offer.value.claimed}
        />
      ) : null;
    };

    const renderEmailStage = () => (
      <div class="space-y-4">
        <div>
          <h1 class="text-xl font-bold text-base-content">
            {t("customer.coins.signup_title")}
          </h1>
          <p class="text-sm text-base-content/60 mt-1">
            {t("customer.coins.signup_subtitle")}
          </p>
        </div>

        <div class="form-control">
          <label class="label" for="signup-email">
            <span class="label-text font-medium">
              {t("customer.coins.signup_email_label")}
            </span>
          </label>
          <input
            id="signup-email"
            type="email"
            inputmode="email"
            autocomplete="email"
            placeholder="you@example.com"
            class="input input-bordered w-full text-base"
            value={email.value}
            onInput={(e) => (email.value = (e.target as HTMLInputElement).value)}
            onKeydown={(e: KeyboardEvent) => e.key === "Enter" && requestCode()}
          />
        </div>

        <label class="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            class="checkbox checkbox-primary checkbox-sm mt-0.5"
            checked={termsAccepted.value}
            onChange={(e) =>
              (termsAccepted.value = (e.target as HTMLInputElement).checked)
            }
          />
          <span class="text-sm text-base-content/70 leading-snug">
            {t("customer.coins.signup_terms_prefix")}{" "}
            <a
              href="/javashani.pdf"
              target="_blank"
              rel="noopener"
              class="link link-primary"
            >
              {t("customer.home.terms_and_conditions")}
            </a>
          </span>
        </label>

        <button
          type="button"
          class="btn btn-primary w-full"
          disabled={!emailValid.value || !termsAccepted.value || loading.value}
          onClick={requestCode}
        >
          {loading.value && <span class="loading loading-spinner loading-sm" />}
          {t("customer.coins.signup_continue")}
        </button>

        <p class="text-xs text-center text-base-content/50">
          {t("customer.coins.signup_no_password")}
        </p>
      </div>
    );

    const renderCodeStage = () => (
      <div class="space-y-4">
        <div>
          <h1 class="text-xl font-bold text-base-content">
            {t("customer.coins.signup_code_title")}
          </h1>
          <p class="text-sm text-base-content/60 mt-1">
            {t("customer.coins.signup_code_subtitle", [email.value.trim()])}
          </p>
        </div>

        <div class="form-control">
          <input
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            placeholder="123456"
            maxlength={6}
            class="input input-bordered w-full text-center text-2xl tracking-[0.5em] font-mono"
            value={code.value}
            onInput={(e) => (code.value = (e.target as HTMLInputElement).value)}
            onKeydown={(e: KeyboardEvent) =>
              e.key === "Enter" && verifyAndClaim()
            }
          />
        </div>

        <button
          type="button"
          class="btn btn-primary w-full gap-2"
          disabled={loading.value || code.value.trim().length < 4}
          onClick={verifyAndClaim}
        >
          {loading.value && <span class="loading loading-spinner loading-sm" />}
          <span class="material-symbols-outlined text-lg">redeem</span>
          {t("customer.coins.signup_verify")}
        </button>

        <button
          type="button"
          class="btn btn-ghost btn-sm w-full"
          disabled={loading.value}
          onClick={() => {
            stage.value = "email";
            code.value = "";
            error.value = null;
          }}
        >
          {t("customer.coins.signup_change_email")}
        </button>
      </div>
    );

    const renderDoneStage = () => (
      <div class="space-y-4 text-center">
        <div class="flex justify-center">
          <span class="material-symbols-outlined text-5xl text-success">
            check_circle
          </span>
        </div>
        <div>
          <h1 class="text-xl font-bold text-base-content">
            {claimedCount.value > 0
              ? t("customer.coins.signup_done_title", [claimedCount.value])
              : t("customer.coins.signup_done_title_generic")}
          </h1>
          <p class="text-sm text-base-content/60 mt-1">
            {t("customer.coins.signup_done_subtitle")}
          </p>
        </div>
        <div class="flex flex-col gap-2">
          <button
            type="button"
            class="btn btn-primary w-full gap-2"
            onClick={() => router.push("/collected")}
          >
            <span class="material-symbols-outlined text-lg">loyalty</span>
            {t("customer.coins.signup_done_collected")}
          </button>
          {paymentGuid.value && (
            <button
              type="button"
              class="btn btn-ghost w-full"
              onClick={() => router.push(`/payments/${paymentGuid.value}`)}
            >
              {t("customer.coins.signup_done_receipt")}
            </button>
          )}
          <button
            type="button"
            class="btn btn-ghost w-full"
            onClick={() => router.push("/")}
          >
            {t("customer.coins.signup_done_explore")}
          </button>
        </div>
      </div>
    );

    return () => (
      <div class="min-h-screen bg-base-200 flex flex-col">
        <div class="flex justify-end p-3">
          <CustomerLanguageSelector languageIds={["fr", "de", "it", "en"]} />
        </div>

        <div class="flex-1 flex items-start justify-center px-4 pb-10">
          <div class="w-full max-w-md space-y-4">
            {renderOffer()}

            <div class="rounded-2xl bg-base-100 border border-base-300 p-5 sm:p-6 shadow-sm">
              {stage.value === "email" && renderEmailStage()}
              {stage.value === "code" && renderCodeStage()}
              {stage.value === "done" && renderDoneStage()}

              {error.value && stage.value !== "done" && (
                <p class="text-sm text-error mt-3 text-center">{error.value}</p>
              )}
              {error.value && stage.value === "done" && (
                <div class="alert alert-warning mt-4 text-sm">
                  <span>{error.value}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
});
